<!-- Global WebSocket Status Indicator -->
@if (shouldShow()) {
<div class="relative">
  <!-- Compact Status Indicator -->
  <button
    type="button"
    class="flex items-center gap-1.5 px-2 py-1 rounded-md transition-all duration-200 hover:bg-bb-bg-tertiary focus:outline-none focus:ring-2 focus:ring-bb-accent-blue/50"
    [class]="statusBgColor()"
    [uiTooltip]="tooltipText()"
    (click)="toggleDetails()"
    [attr.aria-expanded]="showDetails()"
    aria-label="WebSocket connection status"
  >
    <!-- Status Indicator -->
    @switch (statusIcon()) {
      @case ('connected') {
        <span class="w-2 h-2 rounded-full bg-bb-accent-green animate-pulse"></span>
      }
      @case ('connecting') {
        <svg class="w-4 h-4 animate-spin text-bb-accent-amber" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      }
      @case ('degraded') {
        <svg class="w-4 h-4 text-bb-accent-amber" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      }
      @case ('error') {
        <svg class="w-4 h-4 text-bb-accent-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      }
      @default {
        <span class="w-2 h-2 rounded-full bg-bb-text-muted"></span>
      }
    }

    <!-- Queue Badge (if any pending) -->
    @if (queuedCount() > 0) {
      <span class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full bg-bb-accent-amber/15 text-bb-accent-amber">
        {{ queuedCount() }}
      </span>
    }

    <!-- Chevron -->
    <svg
      class="w-3 h-3 text-bb-text-muted transition-transform"
      [class.rotate-180]="showDetails()"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
    </svg>
  </button>

  <!-- Details Panel -->
  @if (showDetails()) {
    <div
      class="absolute right-0 top-full mt-1 w-80 bg-bb-bg-secondary border border-bb-border rounded-lg shadow-xl shadow-black/30 z-dropdown"
      role="region"
      aria-label="WebSocket connection details"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-bb-border">
        <div class="flex items-center gap-2">
          @switch (statusIcon()) {
            @case ('connected') {
              <span class="w-3 h-3 rounded-full bg-bb-accent-green"></span>
            }
            @case ('connecting') {
              <svg class="w-4 h-4 animate-spin text-bb-accent-amber" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            }
            @case ('degraded') {
              <span class="w-3 h-3 rounded-full bg-bb-accent-amber"></span>
            }
            @case ('error') {
              <span class="w-3 h-3 rounded-full bg-bb-accent-red"></span>
            }
            @default {
              <span class="w-3 h-3 rounded-full bg-bb-text-muted"></span>
            }
          }
          <span class="font-medium text-bb-text-primary text-xs font-mono uppercase tracking-wider" data-testid="ws-status-header">{{ statusLabel() }}</span>
        </div>
        <button
          type="button"
          class="p-1 rounded hover:bg-bb-bg-tertiary"
          (click)="hideDetails()"
          aria-label="Close panel"
        >
          <svg class="w-4 h-4 text-bb-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!--
        Per-venue strip (GAP-FE-B003 / GAP-FU-03).
        Renders one row per market venue when ExchangeConnectivityService
        has data; absent on dev/local environments where venues are not
        configured. Pair with the aggregate connection-info block below.
      -->
      @if (venueRows().length > 0) {
        <div class="border-b border-bb-border px-4 py-3">
          <div class="text-2xs font-medium text-bb-text-muted uppercase tracking-wider mb-2">
            Per-venue connections
          </div>
          <app-venue-connection-strip [venues]="venueRows()"></app-venue-connection-strip>
        </div>
      }

      <!-- Connection Info -->
      <div class="px-4 py-3 space-y-2 text-xs">
        @if (state().lastConnected) {
          <div class="flex justify-between">
            <span class="text-bb-text-muted">Last connected:</span>
            <span class="text-bb-text-secondary font-mono">{{ formatTime(state().lastConnected!) }}</span>
          </div>
        }

        @if (state().lastDisconnected && state().status !== 'connected') {
          <div class="flex justify-between">
            <span class="text-bb-text-muted">Last disconnected:</span>
            <span class="text-bb-text-secondary font-mono">{{ formatTime(state().lastDisconnected!) }}</span>
          </div>
        }

        @if (state().status === 'reconnecting') {
          <div class="flex justify-between">
            <span class="text-bb-text-muted">Reconnect attempt:</span>
            <span class="text-bb-text-secondary font-mono">{{ state().reconnectAttempt }} / {{ state().maxReconnectAttempts }}</span>
          </div>
        }

        @if (queuedCount() > 0) {
          <div class="flex justify-between">
            <span class="text-bb-text-muted">Pending updates:</span>
            <span class="text-bb-accent-amber font-medium font-mono">{{ queuedCount() }}</span>
          </div>
        }

        @if (state().usingPollingFallback) {
          <div class="mt-2 p-2 bg-bb-accent-amber/10 rounded text-bb-accent-amber text-2xs">
            Using HTTP polling as fallback. Real-time updates may be delayed.
          </div>
        }

        @if (state().error) {
          <div class="mt-2 p-2 bg-bb-accent-red/10 rounded text-bb-accent-red text-2xs">
            {{ sanitizeError(state().error!) }}
          </div>
        }
      </div>

      <!-- Recent Events -->
      @if (recentEvents().length > 0) {
        <div class="border-t border-bb-border">
          <div class="flex items-center justify-between px-4 py-2">
            <span class="text-2xs font-medium text-bb-text-muted uppercase tracking-wider">Recent Events</span>
            <button
              type="button"
              class="text-2xs text-bb-text-muted hover:text-bb-text-secondary"
              (click)="clearEvents()"
            >
              Clear
            </button>
          </div>
          <div class="max-h-32 overflow-y-auto px-4 pb-3">
            @for (event of recentEvents(); track event.timestamp) {
              <div class="flex items-start gap-2 py-1 text-2xs">
                <span
                  class="capitalize font-medium font-mono"
                  [class]="getEventTypeClass(event.type)"
                >
                  {{ event.type }}
                </span>
                <span class="text-bb-text-muted truncate flex-1">{{ event.endpoint }}</span>
                <span class="text-bb-text-muted whitespace-nowrap font-mono">{{ formatTime(event.timestamp) }}</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</div>
}
